export type DirectionCheckInput = {
  userIntent: string;
  property: string;
  prevValue: string;
  nextValue: string;
};

export type DirectionCheckResult = {
  flip: boolean;
  corrected?: string;
};

const HORIZONTAL_RIGHT = /\b(right|rightward|to the right|further right|move right|nudge it right|push .* right)\b/i;
const HORIZONTAL_LEFT = /\b(left|leftward|to the left|further left|move left|nudge it left|push .* left)\b/i;

type DirectionIntent = 'left' | 'right' | null;

function parseDirection(intent: string): DirectionIntent {
  if (HORIZONTAL_RIGHT.test(intent)) return 'right';
  if (HORIZONTAL_LEFT.test(intent)) return 'left';
  return null;
}

function extractFirstPercent(v: string): number | null {
  const m = /(-?\d+(?:\.\d+)?)\s*%/.exec(v);
  return m ? Number(m[1]) : null;
}

function extractFirstPx(v: string): number | null {
  const m = /(-?\d+(?:\.\d+)?)\s*px/.exec(v);
  if (m) return Number(m[1]);
  // Bare number inside translateX(...) or similar.
  const m2 = /(-?\d+(?:\.\d+)?)/.exec(v);
  return m2 ? Number(m2[1]) : null;
}

/**
 * Visible horizontal shift sign for a property change.
 *   > 0 → visible content moved RIGHT
 *   < 0 → visible content moved LEFT
 *   0   → unknown / not determinable
 */
function horizontalShiftSign(property: string, prev: string, next: string): number {
  const prevNum = extractFirstPercent(prev) ?? extractFirstPx(prev);
  const nextNum = extractFirstPercent(next) ?? extractFirstPx(next);
  if (prevNum == null || nextNum == null) return 0;

  if (property === 'background-position') {
    // Higher % shifts image leftward within the viewport — visible focal point moves left.
    return -(nextNum - prevNum);
  }
  if (property === 'transform') {
    // translateX positive → visible content moves right.
    return nextNum - prevNum;
  }
  if (property === 'margin-left' || property === 'padding-left' || property === 'left') {
    // Increasing left-side offset shifts content rightward.
    return nextNum - prevNum;
  }
  if (property === 'margin-right' || property === 'padding-right' || property === 'right') {
    // Increasing right-side offset shifts content leftward.
    return -(nextNum - prevNum);
  }
  return 0;
}

function mirrorValue(property: string, prevValue: string, nextValue: string): string {
  if (property === 'background-position') {
    // Mirror the first percent around 50% (100 - X).
    return nextValue.replace(/(-?\d+(?:\.\d+)?)\s*%/, (_, n) => `${100 - Number(n)}%`);
  }
  if (property === 'transform') {
    // Flip sign on the first numeric.
    return nextValue.replace(/(-?\d+(?:\.\d+)?)/, (_, n) => `${-Number(n)}`);
  }
  if (property === 'margin-left' || property === 'padding-left' || property === 'left'
    || property === 'margin-right' || property === 'padding-right' || property === 'right') {
    // Mirror around prev: corrected = 2*prev - next.
    const prevNum = extractFirstPx(prevValue);
    const nextNum = extractFirstPx(nextValue);
    if (prevNum == null || nextNum == null) return nextValue;
    const corrected = 2 * prevNum - nextNum;
    return nextValue.replace(/(-?\d+(?:\.\d+)?)\s*px/, `${corrected}px`);
  }
  return nextValue;
}

const CHECKABLE_PROPERTIES = new Set([
  'background-position', 'transform',
  'margin-left', 'margin-right',
  'left', 'right',
  'padding-left', 'padding-right',
]);

export function sanityCheckDirection(input: DirectionCheckInput): DirectionCheckResult {
  if (!CHECKABLE_PROPERTIES.has(input.property)) return { flip: false };
  const intent = parseDirection(input.userIntent);
  if (!intent) return { flip: false };
  const shift = horizontalShiftSign(input.property, input.prevValue, input.nextValue);
  if (shift === 0) return { flip: false };
  const wantsRight = intent === 'right';
  const movesRight = shift > 0;
  if (wantsRight === movesRight) return { flip: false };
  return { flip: true, corrected: mirrorValue(input.property, input.prevValue, input.nextValue) };
}

export type DirectionCorrection = {
  property: string;
  before: string;
  after: string;
};

/**
 * Scan old and new HTML for changes to directional CSS properties and flip the
 * new values when the user's intent disagrees with the visible shift.
 *
 * Only inspects inline `style="..."` declarations — a blunt but narrow scan that
 * covers the common case (LLM-authored section edits). Missed cases (styles in
 * <style> blocks, stylesheets) fall through unchanged.
 *
 * Returns the (possibly corrected) new HTML plus a list of corrections applied.
 */
export function applyDirectionSanityToHtml(
  oldHtml: string,
  newHtml: string,
  userIntent: string,
): { corrected: string; corrections: DirectionCorrection[] } {
  const corrections: DirectionCorrection[] = [];
  let corrected = newHtml;
  for (const prop of CHECKABLE_PROPERTIES) {
    const escaped = prop.replace(/[-]/g, '\\-');
    const valuePattern = new RegExp(`${escaped}\\s*:\\s*([^;"'\\n}]+)`, 'i');
    const oldMatch = valuePattern.exec(oldHtml);
    const newMatch = valuePattern.exec(corrected);
    if (!oldMatch || !newMatch) continue;
    const prevValue = oldMatch[1].trim();
    const nextValue = newMatch[1].trim();
    if (prevValue === nextValue) continue;
    const result = sanityCheckDirection({ userIntent, property: prop, prevValue, nextValue });
    if (!result.flip || !result.corrected) continue;
    const replacePattern = new RegExp(`(${escaped}\\s*:\\s*)([^;"'\\n}]+)`, 'i');
    corrected = corrected.replace(replacePattern, `$1${result.corrected}`);
    corrections.push({ property: prop, before: nextValue, after: result.corrected });
  }
  return { corrected, corrections };
}
